import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Users, Landmark } from "lucide-react";
import { api, LOGO_URL, GROUP_SIZE } from "@/lib/api";
import { ShareButtons } from "@/components/ShareButtons";
import { NextMeetings, useMeetings, MeetingRow } from "@/components/Meetings";
import { BillTracker } from "@/components/bills/BillTracker";
import { BillSources } from "@/components/bills/BillSources";
import { CalendarButtons } from "@/components/CalendarButtons";
import { GroupCard } from "@/components/ElectorateDetail";
import { Footer } from "@/components/Footer";
import { WriteLetterCta } from "@/components/letter/WriteLetterCta";

export default function ElectoratePage() {
  const { name } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["electorate", name],
    queryFn: () => api.get(`/electorates/${encodeURIComponent(name)}`).then((r) => r.data),
  });
  const { data: meetings } = useMeetings(data?.state, name);

  useEffect(() => {
    document.title = `${name} | The R.O.A.N Initiative`;
    return () => { document.title = "The R.O.A.N Initiative | Repatriation Of A Nation"; };
  }, [name]);

  if (isError) {
    return (
      <div className="min-h-screen bg-[#F3EFE4] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="font-type text-2xl text-[#1B2A41]">Electorate not found</p>
        <Link to="/" className="text-[#E2705A] hover:underline">Back to the map</Link>
      </div>
    );
  }

  const groups = {};
  (data?.members || []).forEach((m) => (groups[m.group_number] = [...(groups[m.group_number] || []), m]));
  const groupNums = Object.keys(groups).map(Number).sort((a, b) => a - b);
  if (data && !groupNums.includes(data.next_group)) groupNums.push(data.next_group);

  return (
    <div className="min-h-screen bg-[#F3EFE4]" data-testid="electorate-page">
      <header className="bg-[#121E2B] text-[#FDFBF7] relative grain overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/#map" data-testid="electorate-back-link" className="inline-flex items-center gap-1.5 text-xs font-type uppercase tracking-widest text-[#8FA58A] hover:text-[#E2705A]"><ArrowLeft className="w-3.5 h-3.5" /> All electorates</Link>
            <Link to="/"><img src={LOGO_URL} alt="R.O.A.N" className="h-12 object-contain" /></Link>
          </div>
          <p className="font-type text-xs uppercase tracking-[0.3em] text-[#8FA58A]">Federal electorate · {data?.state || ""}</p>
          <h1 className="font-type font-bold text-4xl sm:text-5xl lg:text-6xl mt-1" data-testid="electorate-page-name">{name}</h1>
          {data?.mp && (
            <p className="mt-3 text-sm sm:text-base text-[#FDFBF7]/80 inline-flex items-center gap-2" data-testid="electorate-page-mp">
              <Landmark className="w-4 h-4 text-[#E2705A]" /> Sitting member: <strong className="text-[#FDFBF7]">{data.mp.name}</strong> · {data.mp.party}
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="font-type text-5xl font-bold text-[#E2705A]" data-testid="electorate-page-count">{data?.count ?? "–"}</span>
              <span className="text-sm text-[#FDFBF7]/80 leading-tight">F.E.R.M{data?.count === 1 ? "" : "s"}<br />signed up</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-type text-5xl font-bold text-[#8FA58A]">{data?.full_groups ?? "–"}</span>
              <span className="text-sm text-[#FDFBF7]/80 leading-tight">group{data?.full_groups === 1 ? "" : "s"}<br />full</span>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/#join" data-testid="electorate-page-join" className="inline-flex items-center px-6 h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] font-type font-bold uppercase tracking-wider text-sm transition-colors">
              <Users className="w-4 h-4 mr-2" /> Join Group {data?.next_group ?? ""}
            </Link>
            {data && <ShareButtons electorate={name} count={data.count} />}
          </div>
          <div className="mt-8 max-w-xl"><WriteLetterCta electorate={name} compact /></div>
        </div>
        <div className="h-2 bg-[#E2705A]" />
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 space-y-14">
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <section data-testid="electorate-page-groups">
            <p className="font-type text-xs uppercase tracking-[0.3em] text-[#5A7355] mb-1">Groups of {GROUP_SIZE}</p>
            <h2 className="font-type font-bold text-2xl sm:text-3xl text-[#1B2A41] mb-4">Who's carrying {name}</h2>
            {isLoading ? <p className="text-sm text-[#5C6A79]">Loading…</p> : (
              <div className="grid sm:grid-cols-2 gap-3">
                {groupNums.map((n) => <GroupCard key={n} number={n} members={groups[n] || []} isNext={n === data.next_group} />)}
              </div>
            )}
          </section>
          <aside className="rounded-2xl bg-[#FAF8F2] border border-[#D8D2C4] p-5" data-testid="electorate-page-meetings">
            <NextMeetings state={data?.state} electorate={name} />
            <div className="mt-4">
              <p className="font-type text-[11px] uppercase tracking-[0.25em] text-[#5A7355] mb-2">Add the series to your calendar</p>
              <CalendarButtons electorate={name} />
            </div>
            {meetings && (
              <details className="mt-4">
                <summary className="text-xs font-semibold text-[#E2705A] cursor-pointer" data-testid="meetings-more-toggle">More dates</summary>
                <ul className="mt-2">
                  {[...(meetings.national || []), ...(meetings.state || []), ...(meetings.division || [])]
                    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
                    .map((m) => <MeetingRow key={m.rule_id + m.starts_at} m={m} />)}
                </ul>
              </details>
            )}
            {data?.postcodes && (
              <p className="mt-5 text-[11px] text-[#5C6A79] leading-relaxed">Postcodes: {data.postcodes.join(", ")}</p>
            )}
          </aside>
        </div>

        <BillSources
          compact
          testId="electorate-bill-sources"
          title="Official bill sources"
          intro={`Live parliamentary pages for the Commonwealth${data?.state ? ` and ${data.state}` : ""}. Check these first, then log what you find below.`}
          codes={["FED", ...(data?.state ? [data.state] : [])]}
        />
        <BillTracker electorate={name} mpName={data?.mp?.name} />
      </main>
      <Footer />
    </div>
  );
}
